import { useCallback, useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import shiningStarsAnimationData from "../assets/shiningstars.json";
import {
  formatProgressDelta,
  formatPointsLabel,
  formatPositivePointsLabel,
  getCelebrationTitle,
  getNeutralProgressMessage,
  getProgressDeltaClassName,
  hasMasteryLoss,
  hasPositiveProgress,
} from "../utils/gameProgressDelta";
import { startLottieStarTwinkle } from "../utils/lottieStarTwinkle";
import { CloverCelebrationNote } from "./CloverRewardScreen";
import "./GameProgressDelta.css";

const CELEBRATION_STARS_DELAY_MS = 500;
const CELEBRATION_STARS_SPEED = 0.55;

const celebrationStarsAnimationData = {
  ...shiningStarsAnimationData,
  layers: shiningStarsAnimationData.layers.filter(
    (layer) => layer.nm !== "Shape Layer 1"
  ),
};

export const CELEBRATION_STARS_DURATION_MS = Math.round(
  (celebrationStarsAnimationData.op / celebrationStarsAnimationData.fr) *
    (1000 / CELEBRATION_STARS_SPEED)
);

function DeltaValue({ value, label }) {
  const className = getProgressDeltaClassName(value);
  return (
    <span className="game-progress-delta__item">
      {label ? <span className="game-progress-delta__label">{label} : </span> : null}
      <strong className={`progress-delta ${className}`}>
        {formatProgressDelta(value)}
      </strong>
    </span>
  );
}

function CelebrationMessage({ discovery, mastery, score, totalQuestions }) {
  const d = Number(discovery);
  const m = Number(mastery);
  const hasDiscovery = Number.isFinite(d) && d > 0;
  const hasMastery = Number.isFinite(m) && m > 0;

  if (hasMasteryLoss(mastery)) {
    return (
      <>
        Vous avez perdu{" "}
        <span className="game-progress-celebration__highlight game-progress-celebration__highlight--negative">
          {formatPointsLabel(m, "maîtrise")}
        </span>
        {hasDiscovery ? (
          <>
            {" "}
            et gagné{" "}
            <span className="game-progress-celebration__highlight">
              {formatPositivePointsLabel(d, "découverte")}
            </span>
          </>
        ) : null}
        . Persévérez pour atteindre les objectifs du niveau suivant.
      </>
    );
  }

  if (hasMastery && hasDiscovery) {
    return (
      <>
        Vous avez gagné{" "}
        <span className="game-progress-celebration__highlight">
          +{m} points de maîtrise
        </span>{" "}
        et{" "}
        <span className="game-progress-celebration__highlight">
          +{d} points de découverte
        </span>
        . Vous vous rapprochez un peu plus du niveau suivant.
      </>
    );
  }

  if (hasMastery) {
    return (
      <>
        Vous avez gagné{" "}
        <span className="game-progress-celebration__highlight">
          +{m} points de maîtrise
        </span>
        . Vous vous rapprochez un peu plus du niveau suivant.
      </>
    );
  }

  if (hasDiscovery) {
    return (
      <>
        Vous avez gagné{" "}
        <span className="game-progress-celebration__highlight">
          +{d} points de découverte
        </span>
        . Votre vocabulaire s&apos;enrichit, continuez ainsi.
      </>
    );
  }

  return <>{getNeutralProgressMessage(score, totalQuestions)}</>;
}

function CelebrationSparkle({ className, onPlay, onComplete }) {
  const lottieRef = useRef(null);
  const twinkleCleanupRef = useRef(null);
  const lastFrame = celebrationStarsAnimationData.op - 1;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const player = lottieRef.current;
      player?.setSpeed(CELEBRATION_STARS_SPEED);
      player?.play();
      onPlay?.();
    }, CELEBRATION_STARS_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      twinkleCleanupRef.current?.();
    };
  }, [onPlay]);

  return (
    <div
      className={`game-progress-celebration__sparkle ${className}`}
      aria-hidden="true"
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={celebrationStarsAnimationData}
        loop={false}
        autoplay={false}
        onComplete={() => {
          const player = lottieRef.current;
          player?.goToAndStop(lastFrame, true);
          twinkleCleanupRef.current?.();
          twinkleCleanupRef.current = startLottieStarTwinkle(
            player?.animationItem
          );
          onComplete?.();
        }}
        className="game-progress-celebration__sparkle-lottie"
        rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
      />
    </div>
  );
}

export function CelebrationSparkles({ onAnimatingChange }) {
  const startedRef = useRef(false);
  const completedRef = useRef(0);

  const handlePlay = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    onAnimatingChange?.(true);
  }, [onAnimatingChange]);

  const handleComplete = useCallback(() => {
    completedRef.current += 1;
    if (completedRef.current >= 2) {
      onAnimatingChange?.(false);
    }
  }, [onAnimatingChange]);

  return (
    <>
      <CelebrationSparkle
        className="game-progress-celebration__sparkle--top-right"
        onPlay={handlePlay}
        onComplete={handleComplete}
      />
      <CelebrationSparkle
        className="game-progress-celebration__sparkle--bottom-left"
        onPlay={handlePlay}
        onComplete={handleComplete}
      />
    </>
  );
}

function CelebrationLayout({
  discovery,
  mastery,
  score,
  totalQuestions,
  cloverPotionId = null,
  className = "",
}) {
  const masteryLost = hasMasteryLoss(mastery);
  const positiveProgress = hasPositiveProgress(discovery, mastery);
  const showSparkles = positiveProgress && !masteryLost;
  const title = getCelebrationTitle(discovery, mastery);
  const [isAnimating, setIsAnimating] = useState(false);

  return (
    <div
      className={`game-progress-celebration${
        !positiveProgress || masteryLost ? " game-progress-celebration--plain" : ""
      }${
        showSparkles && isAnimating ? " game-progress-celebration--animating" : ""
      } ${className}`.trim()}
      style={
        showSparkles
          ? {
              "--celebration-shimmer-duration": `${CELEBRATION_STARS_DURATION_MS}ms`,
            }
          : undefined
      }
    >
      {showSparkles ? (
        <CelebrationSparkles onAnimatingChange={setIsAnimating} />
      ) : null}
      {title ? (
        <p className="game-progress-celebration__title">{title}</p>
      ) : null}
      <p className="game-progress-celebration__message">
        <CelebrationMessage
          discovery={discovery}
          mastery={mastery}
          score={score}
          totalQuestions={totalQuestions}
        />
      </p>
      {cloverPotionId ? <CloverCelebrationNote cloverPotionId={cloverPotionId} /> : null}
    </div>
  );
}

export default function GameProgressDelta({
  discovery,
  mastery,
  score,
  totalQuestions,
  layout = "inline",
  cloverPotionId = null,
  className = "",
}) {
  const discoveryValue = Number(discovery) || 0;
  const masteryValue = Number(mastery) || 0;

  if (layout === "celebration") {
    return (
      <CelebrationLayout
        discovery={discoveryValue}
        mastery={masteryValue}
        score={score}
        totalQuestions={totalQuestions}
        cloverPotionId={cloverPotionId}
        className={className}
      />
    );
  }

  return (
    <div className={`game-progress-delta game-progress-delta--${layout} ${className}`.trim()}>
      <DeltaValue value={discoveryValue} label="Découverte" />
      <DeltaValue value={masteryValue} label="Maîtrise" />
    </div>
  );
}

export function ProgressDeltaCell({ value }) {
  const className = getProgressDeltaClassName(value);
  const num = Number(value);
  const display =
    Number.isFinite(num) ? formatProgressDelta(num) : "—";

  return (
    <strong className={`progress-delta ${Number.isFinite(num) ? className : "progress-delta--na"}`}>
      {display}
    </strong>
  );
}
